import { useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Glass chrome switch. true: the floating chrome (top bars, tab bars, sheet
 * scrims, toasts) renders as frosted glass with content scrolling under it
 * (client/src/styles/glass-chrome.css). false: the app renders exactly as it
 * did before glass, because every glass rule is scoped under
 * :root[data-glass="on"].
 */
export const GLASS_CHROME_ENABLED = true;

if (typeof document !== 'undefined') {
  if (GLASS_CHROME_ENABLED) document.documentElement.dataset.glass = 'on';
  else delete document.documentElement.dataset.glass;
}

const BAR_SELECTOR = '.glass-top-bar';

/**
 * Measures the shell's visible top bar and publishes its height as
 * --glass-top-h, plus data-glass-top="measured" on the shell. The bar floats
 * over the scroller only while that attribute is set (glass-chrome.css), so a
 * shell that has not been measured keeps its bar in flow: content can never
 * sit under an unmeasured bar.
 *
 * The bar owns the status-bar inset (its own padding-top), so the height
 * already includes it and the scroller never adds the inset a second time.
 *
 * Kept current by a ResizeObserver on every bar (font loads, safe-area
 * changes, the bar's contents changing), a MutationObserver for bars that
 * mount or unmount, and resize, orientation and visual viewport listeners.
 * Returns the cleanup.
 */
export function bindGlassChrome(shell: HTMLElement): () => void {
  let bars: HTMLElement[] = [];
  let frame = 0;
  const resizeObserver = new ResizeObserver(() => measure());

  const measure = () => {
    const height = Math.max(0, ...bars.map((bar) => bar.getBoundingClientRect().height));
    if (height > 0) {
      // Writes only on a change, so a re-measure never restyles the page.
      if (shell.style.getPropertyValue('--glass-top-h') !== `${height}px`) {
        shell.style.setProperty('--glass-top-h', `${height}px`);
      }
      if (shell.dataset.glassTop !== 'measured') shell.dataset.glassTop = 'measured';
    } else if (shell.dataset.glassTop) {
      shell.style.removeProperty('--glass-top-h');
      delete shell.dataset.glassTop;
    }
  };

  const collect = () => {
    const next = Array.from(shell.querySelectorAll<HTMLElement>(BAR_SELECTOR));
    if (next.length !== bars.length || next.some((bar, i) => bar !== bars[i])) {
      resizeObserver.disconnect();
      bars = next;
      bars.forEach((bar) => resizeObserver.observe(bar));
    }
    measure();
  };

  // Re-measure after layout has settled (next frame), coalescing bursts.
  const schedule = () => {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      collect();
    });
  };

  const mutationObserver = new MutationObserver(schedule);
  mutationObserver.observe(shell, { childList: true, subtree: true });
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  window.visualViewport?.addEventListener('resize', schedule);

  collect();
  schedule();

  return () => {
    if (frame) cancelAnimationFrame(frame);
    resizeObserver.disconnect();
    mutationObserver.disconnect();
    window.removeEventListener('resize', schedule);
    window.removeEventListener('orientationchange', schedule);
    window.visualViewport?.removeEventListener('resize', schedule);
    shell.style.removeProperty('--glass-top-h');
    delete shell.dataset.glassTop;
  };
}

/**
 * Binds `bind` to the element in `ref`, and rebinds whenever that element
 * changes. A layout can swap its shell element without unmounting (the coach
 * layout renders the training shell in its place and back), so binding once
 * on mount would leave the new shell unwatched.
 */
export function useBoundElement(ref: RefObject<HTMLElement | null>, bind: (el: HTMLElement) => () => void, enabled = true) {
  const bound = useRef<{ el: HTMLElement; dispose: () => void } | null>(null);
  useLayoutEffect(() => {
    const el = enabled ? ref.current : null;
    if (bound.current?.el === el) return;
    bound.current?.dispose();
    bound.current = el ? { el, dispose: bind(el) } : null;
  });
  useLayoutEffect(() => () => {
    bound.current?.dispose();
    bound.current = null;
  }, []);
}

/** Publishes the shell's top bar height for the glass scroll-under layout. */
export function useGlassChrome(shellRef: RefObject<HTMLElement | null>) {
  useBoundElement(
    shellRef,
    bindGlassChrome,
    GLASS_CHROME_ENABLED && typeof ResizeObserver !== 'undefined' && typeof MutationObserver !== 'undefined',
  );
}
